export type ErrorReporter = (payload: Record<string, unknown>) => void;

export interface InstallErrorHandlersOptions {
  reporter?: ErrorReporter;
  /**
   * Optional callback invoked once per captured error/rejection. Used by
   * the metrics collector to count errors without coupling this module
   * to it directly. Errors thrown by the counter are swallowed.
   */
  onError?: () => void;
}

/**
 * Capture uncaught errors and unhandled promise rejections on the page.
 * Reports to the provided reporter (e.g. Sentry or a beacon) and falls
 * back to `console.error` so M0 has visible signal in DevTools.
 */
export function installErrorHandlers(options: InstallErrorHandlersOptions = {}): void {
  const reporter = options.reporter;
  const onError = options.onError;

  const send = (level: 'error' | 'unhandledrejection', detail: unknown): void => {
    const payload: Record<string, unknown> = {
      level,
      message: detail instanceof Error ? detail.message : String(detail),
      stack: detail instanceof Error ? detail.stack : undefined,
      url: location.href,
      ts: Date.now(),
    };
    console.error('[error-capture]', payload);
    reporter?.(payload);
    if (onError) {
      try {
        onError();
      } catch (err) {
        console.error('[error-capture] counter callback threw', err);
      }
    }
  };

  window.addEventListener('error', (event) => {
    send('error', event.error ?? event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    send('unhandledrejection', event.reason);
  });
}
